import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SegmentedControl,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { Field } from '@vnetwork/ui/forms';
import {
  useCreateScheduledAction,
  useUpdateScheduledAction,
  useInstanceSnapshots,
  type Instance,
  type ScheduledAction,
  type ScheduledActionPayload,
} from '@/api/opennebula';

const ACTION_OPTIONS = [
  { value: 'backup', label: 'Backup' },
  { value: 'poweroff', label: 'Power Off' },
  { value: 'poweroff-hard', label: 'Power Off Hard' },
  { value: 'reboot', label: 'Reboot' },
  { value: 'reboot-hard', label: 'Reboot Hard' },
  { value: 'stop', label: 'Stop' },
  { value: 'suspend', label: 'Suspend' },
  { value: 'resume', label: 'Resume' },
  { value: 'disk-snapshot-create', label: 'Disk Snapshot Create' },
  { value: 'disk-snapshot-delete', label: 'Disk Snapshot Delete' },
  { value: 'disk-snapshot-revert', label: 'Disk Snapshot Revert' },
];
const FREQUENCIES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const REPEAT_CODE: Record<string, number> = { weekly: 0, monthly: 1, yearly: 2, hourly: 3 };
const END_CODE: Record<string, number> = { never: 0, after: 1, on: 2 };

const needsDisk = (a: string) => a.startsWith('disk-snapshot-');
const needsSnapshot = (a: string) => a === 'disk-snapshot-delete' || a === 'disk-snapshot-revert';

type ScheduleType = 'one-time' | 'periodic';
type EndType = 'never' | 'after' | 'on';

export function ScheduledActionDialog({
  open,
  onOpenChange,
  serviceId,
  instance,
  editing,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  instance: Instance;
  editing?: ScheduledAction | null;
}) {
  const { show } = useToast();
  const disks = instance.metadata?.disks ?? [];
  const { data: snapshots = [] } = useInstanceSnapshots(serviceId, instance.id);

  const [action, setAction] = useState('backup');
  const [scheduleType, setScheduleType] = useState<ScheduleType>('periodic');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('16:00');
  const [frequency, setFrequency] = useState('weekly');
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [hourlyInterval, setHourlyInterval] = useState('1');
  const [monthlyDays, setMonthlyDays] = useState('1');
  const [yearlyDays, setYearlyDays] = useState('1');
  const [endType, setEndType] = useState<EndType>('never');
  const [afterTimes, setAfterTimes] = useState('1');
  const [endDate, setEndDate] = useState('');
  const [diskId, setDiskId] = useState('');
  const [snapshotName, setSnapshotName] = useState('');
  const [snapshotId, setSnapshotId] = useState('');

  // Prefill on edit (best-effort parse of the stored schedule).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setAction(editing.schedule_action || 'backup');
      const epoch = Number(editing.time) * 1000;
      if (Number.isFinite(epoch) && epoch > 0) {
        const d = new Date(epoch);
        setDate(d.toISOString().slice(0, 10));
        setTime(d.toTimeString().slice(0, 5));
      }
      const rep = editing.repeat;
      if (rep == null || rep === '') {
        setScheduleType('one-time');
      } else {
        setScheduleType('periodic');
        const freq = Object.entries(REPEAT_CODE).find(([, c]) => String(c) === String(rep))?.[0] ?? 'weekly';
        setFrequency(freq);
        if (freq === 'weekly') setSelectedDays((editing.days ?? '').split(',').filter(Boolean));
        if (freq === 'hourly') setHourlyInterval(editing.days || '1');
        if (freq === 'monthly') setMonthlyDays(editing.days || '1');
        if (freq === 'yearly') setYearlyDays(editing.days || '1');
      }
      const et = Object.entries(END_CODE).find(([, c]) => String(c) === String(editing.end_type))?.[0] as EndType;
      setEndType(et ?? 'never');
      if (et === 'after') setAfterTimes(editing.end_value || '1');
    } else {
      setAction('backup');
      setScheduleType('periodic');
      setFrequency('weekly');
      setSelectedDays([]);
      setEndType('never');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  // disk-snapshot-delete can only run once.
  useEffect(() => {
    if (action === 'disk-snapshot-delete') setScheduleType('one-time');
  }, [action]);

  const create = useCreateScheduledAction(serviceId, instance.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Schedule created' }); onOpenChange(false); },
    onError: () => show({ tone: 'danger', title: 'Create failed' }),
  });
  const update = useUpdateScheduledAction(serviceId, instance.id, {
    onSuccess: () => { show({ tone: 'success', title: 'Schedule updated' }); onOpenChange(false); },
    onError: () => show({ tone: 'danger', title: 'Update failed' }),
  });
  const pending = create.isPending || update.isPending;

  const availableSnapshots = useMemo(
    () => snapshots.filter((s) => String(s.disk_id) === String(diskId)),
    [snapshots, diskId],
  );

  const toggleDay = (i: number) => {
    const v = String(i);
    setSelectedDays((prev) => (prev.includes(v) ? prev.filter((d) => d !== v) : [...prev, v]));
  };

  const buildPayload = (): ScheduledActionPayload | null => {
    if (!date || !time) return null;
    const timeUnix = Math.floor(new Date(`${date}T${time}:00`).getTime() / 1000);
    if (!Number.isFinite(timeUnix)) return null;

    const payload: ScheduledActionPayload = { schedule_action: action, time: timeUnix, end_type: 0, end_value: '1' };

    // args for disk-snapshot operations
    if (needsDisk(action)) {
      if (!diskId) return null;
      if (action === 'disk-snapshot-create') payload.args = snapshotName ? `${diskId},${snapshotName}` : diskId;
      else if (needsSnapshot(action)) {
        if (!snapshotId) return null;
        payload.args = `${diskId},${snapshotId}`;
      } else payload.args = diskId;
    }

    if (scheduleType === 'one-time') {
      payload.end_type = 2;
      payload.end_value = timeUnix;
      return payload;
    }

    payload.repeat = REPEAT_CODE[frequency] ?? 0;
    if (frequency === 'hourly') payload.days = hourlyInterval || '1';
    else if (frequency === 'weekly') payload.days = (selectedDays.length ? [...selectedDays].sort((a, b) => +a - +b) : ['1', '2', '3', '4', '5']).join(',');
    else if (frequency === 'monthly') payload.days = monthlyDays || '1';
    else payload.days = yearlyDays || '1';

    payload.end_type = END_CODE[endType];
    if (endType === 'after') payload.end_value = afterTimes || '1';
    else if (endType === 'on' && endDate) payload.end_value = Math.floor(new Date(`${endDate}T00:00:00`).getTime() / 1000);
    else payload.end_value = '1';
    return payload;
  };

  const submit = () => {
    const payload = buildPayload();
    if (!payload) { show({ tone: 'danger', title: 'Please complete the schedule' }); return; }
    if (editing) update.mutate({ id: editing.id, payload });
    else create.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit scheduled action' : 'New scheduled action'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <Field label="Action">
              {(p) => (
                <Select value={action} onValueChange={setAction}>
                  <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ACTION_OPTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </Field>

            {needsDisk(action) && (
              <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: '1fr 1fr' }}>
                <Field label="Disk">
                  {(p) => (
                    <Select value={diskId} onValueChange={setDiskId}>
                      <SelectTrigger {...p}><SelectValue placeholder="Select disk" /></SelectTrigger>
                      <SelectContent>
                        {disks.map((d) => <SelectItem key={d.disk_id} value={String(d.disk_id)}>disk-{d.disk_id}{d.image ? ` (${d.image})` : ''}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
                {action === 'disk-snapshot-create' ? (
                  <Field label="Snapshot name (optional)">
                    {(p) => <Input {...p} value={snapshotName} onChange={(e) => setSnapshotName(e.target.value)} placeholder="auto" />}
                  </Field>
                ) : (
                  <Field label="Snapshot">
                    {(p) => (
                      <Select value={snapshotId} onValueChange={setSnapshotId}>
                        <SelectTrigger {...p}><SelectValue placeholder="Select snapshot" /></SelectTrigger>
                        <SelectContent>
                          {availableSnapshots.map((s) => <SelectItem key={String(s.id)} value={String(s.id)}>{s.name || `snapshot-${s.id}`}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                )}
              </div>
            )}

            <SegmentedControl
              value={scheduleType}
              onValueChange={(v) => setScheduleType(v as ScheduleType)}
              items={[{ value: 'one-time', label: 'One-time' }, { value: 'periodic', label: 'Periodic' }]}
            />

            <div style={{ display: 'grid', gap: 'var(--vn-space-3)', gridTemplateColumns: '1fr 1fr' }}>
              <Field label="Start date" required>{(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
              <Field label="Start time" required>{(p) => <Input {...p} type="time" value={time} onChange={(e) => setTime(e.target.value)} />}</Field>
            </div>

            {scheduleType === 'periodic' && (
              <>
                <Field label="Frequency">
                  {(p) => (
                    <Select value={frequency} onValueChange={setFrequency}>
                      <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </Field>

                {frequency === 'weekly' && (
                  <Field label="Days of week">
                    {() => (
                      <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                        {WEEKDAYS.map((d, i) => (
                          <Button key={d} size="sm" variant={selectedDays.includes(String(i)) ? 'primary' : 'secondary'} onClick={() => toggleDay(i)}>{d}</Button>
                        ))}
                      </div>
                    )}
                  </Field>
                )}
                {frequency === 'hourly' && (
                  <Field label="Every N hours">{(p) => <Input {...p} type="number" min={1} max={24} value={hourlyInterval} onChange={(e) => setHourlyInterval(e.target.value)} />}</Field>
                )}
                {frequency === 'monthly' && (
                  <Field label="Day(s) of month (1-31, comma-separated)">{(p) => <Input {...p} value={monthlyDays} onChange={(e) => setMonthlyDays(e.target.value)} />}</Field>
                )}
                {frequency === 'yearly' && (
                  <Field label="Day(s) of year (1-365, comma-separated)">{(p) => <Input {...p} value={yearlyDays} onChange={(e) => setYearlyDays(e.target.value)} />}</Field>
                )}

                <Field label="Ends">
                  {() => (
                    <>
                      <SegmentedControl
                        value={endType}
                        onValueChange={(v) => setEndType(v as EndType)}
                        items={[{ value: 'never', label: 'Never' }, { value: 'after', label: 'After' }, { value: 'on', label: 'On date' }]}
                      />
                      {endType === 'after' && (
                        <div style={{ marginTop: 'var(--vn-space-2)' }}><Input type="number" min={1} value={afterTimes} onChange={(e) => setAfterTimes(e.target.value)} placeholder="Repetitions" /></div>
                      )}
                      {endType === 'on' && (
                        <div style={{ marginTop: 'var(--vn-space-2)' }}><Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></div>
                      )}
                    </>
                  )}
                </Field>
              </>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={pending} onClick={submit}>{editing ? 'Save changes' : 'Create'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Human-readable summary of a stored schedule for the table. */
export function describeSchedule(s: ScheduledAction): { repeat: string; ends: string } {
  if (s.repeat == null || s.repeat === '') return { repeat: 'One-time', ends: '—' };
  const freq = Object.entries(REPEAT_CODE).find(([, c]) => String(c) === String(s.repeat))?.[0] ?? 'custom';
  const repeat = `${freq[0].toUpperCase()}${freq.slice(1)}${s.days ? ` (${s.days})` : ''}`;
  const ends = s.end_type === '0' ? 'Never' : s.end_type === '1' ? `After ${s.end_value}` : s.end_type === '2' ? 'On date' : '—';
  return { repeat, ends };
}

export function formatScheduleTime(epoch?: string): string {
  const ms = Number(epoch) * 1000;
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return new Date(ms).toLocaleString();
}
